
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import PasswordField from "./PasswordField";
import { supabase } from "@/lib/supabase";
import { CheckCircle, Shield, Lock } from "lucide-react";

interface PasswordResetFormProps {
  onSuccess?: () => void;
}

const PasswordResetForm = ({ onSuccess }: PasswordResetFormProps) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [resetError, setResetError] = useState("");
  
  const { toast } = useToast();

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError("");

    if (!newPassword || !confirmPassword) {
      setResetError("Both password fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setResetError("Password must be at least 8 characters");
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setResetError("Password must contain at least one uppercase letter, one lowercase letter, and one number");
      return;
    }

    setIsResetting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        setResetError(error.message);
        return;
      }

      toast({
        title: "Password updated successfully",
        description: "Your SnapRefund account is now secure with your new password.",
      });

      setIsSuccess(true);

    } catch (error: any) {
      console.error("Password reset error:", error);
      setResetError("Something went wrong. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  const handleGoToDashboard = () => {
    if (onSuccess) {
      onSuccess();
    }
  };

  if (isSuccess) {
    return (
      <div className="container flex items-center justify-center min-h-screen py-8">
        <Card className="w-full max-w-md mx-auto shadow-lg">
          <CardHeader className="space-y-1 text-center bg-gradient-to-br from-green-50 to-emerald-50 rounded-t-lg">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-xl font-bold bg-gradient-to-r from-[#0F4C81] to-teal-600 bg-clip-text text-transparent">
                SnapRefund
              </span>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Password Updated!</CardTitle>
            <CardDescription className="text-slate-600">
              Your account is now secure with your new password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center p-6">
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Account Secured</span>
              </div>
              <p className="text-sm text-green-700">
                You can now access your SnapRefund dashboard with your new password and continue managing your refund requests.
              </p>
            </div>
            <Button 
              onClick={handleGoToDashboard}
              className="w-full bg-gradient-to-r from-[#0F4C81] to-teal-600 hover:from-[#0F4C81]/90 hover:to-teal-700 text-white font-medium"
            >
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container flex items-center justify-center min-h-screen py-8">
      <Card className="w-full max-w-md mx-auto shadow-lg">
        <CardHeader className="space-y-1 text-center bg-gradient-to-br from-blue-50 to-teal-50 rounded-t-lg">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-xl font-bold bg-gradient-to-r from-[#0F4C81] to-teal-600 bg-clip-text text-transparent">
              SnapRefund
            </span>
          </div>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <Lock className="h-6 w-6 text-[#0F4C81]" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">Set a new password</CardTitle>
          <CardDescription className="text-slate-600">
            Create a strong password for your SnapRefund account
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <PasswordField
              id="newPassword"
              label="New Password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (resetError) setResetError("");
              }}
              required
              minLength={8}
              showStrengthIndicator={true}
            />

            <PasswordField
              id="confirmPassword"
              label="Confirm Password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (resetError) setResetError("");
              }}
              required
              minLength={8}
            />

            <div className="bg-amber-50 p-3 rounded-lg border border-amber-100">
              <div className="flex items-start gap-2">
                <Shield className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-amber-800 mb-1">Password Requirements</p>
                  <p className="text-xs text-amber-700">
                    • At least 8 characters<br/>
                    • One uppercase & lowercase letter<br/>
                    • At least one number
                  </p>
                </div>
              </div>
            </div>

            {resetError && (
              <div className="bg-red-50 p-3 rounded-lg border border-red-100">
                <p className="text-sm text-red-700 flex items-center gap-1">
                  <span className="text-xs">⚠️</span>
                  {resetError}
                </p>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-[#0F4C81] to-teal-600 hover:from-[#0F4C81]/90 hover:to-teal-700 text-white font-medium" 
              disabled={isResetting}
            >
              {isResetting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Update password
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PasswordResetForm;
